import React, { useContext, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  Act,
  Attach,
  Back,
  Burger,
  Close,
  Coins,
  Delete,
  List,
  Person,
  PhoneTransparent,
  Pulse,
  Statements,
} from '@pulse-web-ui/icons';
import { useTheme } from '@pulse-web-ui/theme';
import { observer } from 'mobx-react-lite';

import {
  ABOUT,
  AnalyticEvent,
  CANDIDATE_AUTH,
  CONTACTS,
  MAIN,
  PROFILE,
  ProfileType,
  SRG_LINK,
  STANDARDS,
  TERMINATION_CONTRACT,
} from '@shared/constants';
import { useStores } from '@shared/hooks';
import { ProductCode } from '@shared/types';
import { IconButton } from '@shared/ui';
import { addTestAttribute } from '@shared/utils';

import rgsLogo from '../../assets/images/rgs-logo.svg';
import { UserContext } from '../contexts/user-context';
import { sendAnalyticEvent } from '../web-analytic';
import {
  HeaderButtons,
  HeaderContainer,
  HeaderNav,
  IconContainer,
  LogoLink,
  LogoutButton,
  MortgageApplications,
  NavItemLink,
  NavList,
  RgsLogo,
} from './header.styles';

const NEWS_AND_EDUCATION_LINK =
  'https://dashing-consonant-efe.notion.site/d31676b16c4e44c69e1bca95e775560c';

export const Header = observer(() => {
  const {
    MainStore: {
      productsStore: { products },
      agentUserStore: { availableSign, userKind },
    },
  } = useStores();
  const theme = useTheme();
  const { t } = useTranslation();
  const { isAuthenticated } = useContext(UserContext);
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();

  const isCandidateAuth = searchParams.get('isCandidateAuth');
  const [isNavDisplayed, setIsNavDisplayed] = useState(false);

  const showMortgageApplications = useMemo(
    () =>
      !!products?.find(
        ({ productCode }) => productCode === ProductCode.MORTGAGE
      ),
    [products]
  );

  const isAgent =
    userKind &&
    [ProfileType.AGENT, ProfileType.AGENT_MVP].includes(userKind.profileType);

  const isTerminationAvailable =
    availableSign?.contractTermination?.isTerminationAvailable;

  const logoLink = useMemo(() => {
    if (isAuthenticated && isAgent) {
      return MAIN;
    }

    if (pathname === CANDIDATE_AUTH || isCandidateAuth) {
      return CANDIDATE_AUTH;
    }

    return `${pathname}${search}`;
  }, [isAuthenticated, isAgent, pathname, search]);

  const toggleNav = () => {
    if (isNavDisplayed) {
      document.body.style.overflow = 'initial';
    } else {
      document.body.style.overflow = 'hidden';
    }
    setIsNavDisplayed(!isNavDisplayed);
  };

  const closeMenu = () => {
    document.body.style.overflow = 'initial';
    setIsNavDisplayed(false);
  };

  const handleMenuItemClick = (event?: AnalyticEvent) => () => {
    event && sendAnalyticEvent(event);
    closeMenu();
  };

  return (
    <>
      <HeaderContainer>
        <LogoLink
          onClick={closeMenu}
          to={logoLink}
          reloadDocument={isAgent}
        >
          <RgsLogo
            src={rgsLogo}
            alt='Logo'
          />
        </LogoLink>
        {isAuthenticated && (
          <HeaderButtons>
            {isAgent && (
              <Link
                to={PROFILE}
                onClick={handleMenuItemClick(AnalyticEvent.AGENT_PROFILE_M)}
                {...addTestAttribute('header-profile')}
              >
                <IconButton>
                  <Person
                    width={18}
                    color={theme.colors.icon.primary}
                  />
                </IconButton>
              </Link>
            )}
            <IconButton
              onClick={toggleNav}
              {...addTestAttribute('header-nav')}
            >
              {isNavDisplayed ? (
                <Close
                  width={20}
                  color={theme.colors.icon.primary}
                />
              ) : (
                <Burger
                  width={18}
                  color={theme.colors.icon.primary}
                />
              )}
            </IconButton>
          </HeaderButtons>
        )}
      </HeaderContainer>
      {isNavDisplayed && (
        <HeaderNav>
          {isAgent && (
            <NavList>
              <li>
                <NavItemLink
                  to='/sales'
                  onClick={handleMenuItemClick(AnalyticEvent.AGENT_SALES_M)}
                  {...addTestAttribute('navigation-sales')}
                >
                  <IconButton>
                    <Coins
                      width={24}
                      color={theme.colors.icon.primary}
                    />
                  </IconButton>
                  <span>{t('COMMON:navigation.sales')}</span>
                </NavItemLink>
              </li>
              <li>
                <NavItemLink
                  to='/acts'
                  onClick={handleMenuItemClick(AnalyticEvent.AGENT_STATEMENT_M)}
                  {...addTestAttribute('navigation-acts')}
                >
                  <IconButton>
                    <Act
                      width={24}
                      color={theme.colors.icon.primary}
                      viewBox='0 0 24 24'
                    />
                  </IconButton>
                  <span>{t('COMMON:navigation.acts')}</span>
                </NavItemLink>
              </li>
              <li>
                <NavItemLink
                  to={ABOUT}
                  onClick={handleMenuItemClick(
                    AnalyticEvent.AGENT_ABOUTPRODUCTS_M
                  )}
                  {...addTestAttribute('navigation-about')}
                >
                  <IconButton>
                    <Pulse
                      width={24}
                      color={theme.colors.icon.primary}
                    />
                  </IconButton>
                  <span>{t('COMMON:navigation.about')}</span>
                </NavItemLink>
              </li>
              <li>
                <NavItemLink
                  to={CONTACTS}
                  onClick={handleMenuItemClick(AnalyticEvent.AGENT_CONTACTS_M)}
                  {...addTestAttribute('navigation-contacts')}
                >
                  <IconButton>
                    <PhoneTransparent
                      width={24}
                      color={theme.colors.icon.primary}
                    />
                  </IconButton>
                  <span>{t('COMMON:navigation.contacts')}</span>
                </NavItemLink>
              </li>
              <li>
                <NavItemLink
                  to={NEWS_AND_EDUCATION_LINK}
                  onClick={handleMenuItemClick(AnalyticEvent.AGENT_NEWS_M)}
                  target='_blank'
                >
                  <IconButton>
                    <List
                      width={24}
                      viewBox='0 0 16 24'
                      color={theme.colors.icon.primary}
                    />
                  </IconButton>
                  <span>{t('COMMON:navigation.newsAndEducation')}</span>
                </NavItemLink>
              </li>
              <li>
                <NavItemLink
                  to={STANDARDS}
                  onClick={handleMenuItemClick(
                    AnalyticEvent.AGENT_STANDARDSVSS_M
                  )}
                  {...addTestAttribute('navigation-standards')}
                >
                  <IconButton>
                    <Statements
                      width={24}
                      viewBox='0 0 24 24'
                      color={theme.colors.icon.primary}
                    />
                  </IconButton>
                  <span>{t('COMMON:navigation.agentStandards')}</span>
                </NavItemLink>
              </li>
              {showMortgageApplications && (
                <li>
                  <NavItemLink
                    to={SRG_LINK}
                    onClick={handleMenuItemClick(
                      AnalyticEvent.AGENT_MORTAGAGEREQUEST_M
                    )}
                    target='_blank'
                  >
                    <IconButton>
                      <Attach
                        width={24}
                        viewBox='0 0 24 24'
                        color={theme.colors.icon.primary}
                      />
                    </IconButton>
                    <MortgageApplications>
                      {t('COMMON:navigation.mortgageApplications')}
                      <IconContainer>
                        <Back
                          width={16}
                          color={theme.colors.icon.primary}
                        />
                      </IconContainer>
                    </MortgageApplications>
                  </NavItemLink>
                </li>
              )}
            </NavList>
          )}
          <NavList>
            {isTerminationAvailable && (
              <NavItemLink
                to={TERMINATION_CONTRACT}
                onClick={handleMenuItemClick(AnalyticEvent.AGENT_TERMINATE_M)}
                {...addTestAttribute('navigation-termination-contract')}
              >
                <IconButton>
                  <Delete
                    width={24}
                    color={theme.colors.icon.primary}
                  />
                </IconButton>
                <span>{t('COMMON:navigation.terminationContract')}</span>
              </NavItemLink>
            )}
            <LogoutButton
              onLogout={handleMenuItemClick(AnalyticEvent.AGENT_EXIT_M)}
            />
          </NavList>
        </HeaderNav>
      )}
    </>
  );
});
